import { useState } from "react";
import { Send, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const GuestApplication = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    topic: "",
    about: "",
    social: "",
  });
  const [status, setStatus] = useState({ type: null, message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.topic) {
      setStatus({ type: "error", message: "Please fill in all required fields" });
      return;
    }

    setLoading(true);
    setStatus({ type: null, message: "" });

    try {
      await axios.post(`${BACKEND_URL}/api/guest-applications`, formData);
      setStatus({ type: "success", message: "Application submitted! We'll be in touch if it's a fit." });
      setFormData({ name: "", email: "", topic: "", about: "", social: "" });
    } catch (error) {
      setStatus({ type: "error", message: "Something went wrong. Try again?" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="be-a-guest" className="px-5 sm:px-8 lg:px-12 py-14 sm:py-20 md:py-32" data-testid="guest-application-section">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Left — Pitch */}
        <div className="min-w-0">
          <p className="overline-tcc mb-4">Apply to be a guest</p>
          <h2 className="font-display text-4xl sm:text-6xl md:text-7xl leading-[0.9] text-[#F4F2EE] mb-8 break-words">
            Got a story?<br />
            <span className="text-[#FF3E8A]">Sit down.</span>
          </h2>
          <p className="text-[#9494A6] font-light text-base md:text-lg leading-relaxed max-w-md mb-10">
            We're looking for humans with interesting perspectives on life, work, creativity,
            or the beautiful chaos in between.
          </p>
          <ul className="space-y-3">
            {[
              "No need to be famous or polished",
              "Authenticity over expertise",
              "Recorded at Savi Coliving, Valencia",
            ].map((line, i) => (
              <li key={i} className="flex items-baseline gap-4 font-mono-tcc text-xs uppercase tracking-[0.15em] text-[#9494A6]">
                <span className="text-[#C8FF3E] shrink-0">→</span>
                {line}
              </li>
            ))}
          </ul>
        </div>

        {/* Right — Form */}
        <form onSubmit={handleSubmit} noValidate className="space-y-8 border border-[#232332] p-6 sm:p-10 md:p-12">
          <div>
            <label className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#9494A6] block mb-1">
              Your name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="input-brutal"
              placeholder="What should we call you?"
              data-testid="guest-name-input"
            />
          </div>

          <div>
            <label className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#9494A6] block mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="input-brutal"
              placeholder="your@email.com"
              data-testid="guest-email-input"
            />
          </div>

          <div>
            <label className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#9494A6] block mb-1">
              What would you talk about? *
            </label>
            <input
              type="text"
              name="topic"
              value={formData.topic}
              onChange={handleChange}
              className="input-brutal"
              placeholder="Your messy middle story..."
              data-testid="guest-topic-input"
            />
          </div>

          <div>
            <label className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#9494A6] block mb-1">
              Tell us about yourself
            </label>
            <textarea
              name="about"
              value={formData.about}
              onChange={handleChange}
              rows={2}
              className="input-brutal resize-none"
              placeholder="A few sentences about who you are..."
              data-testid="guest-about-input"
            />
          </div>

          <div>
            <label className="font-mono-tcc text-[10px] uppercase tracking-[0.25em] text-[#9494A6] block mb-1">
              Social / Website
            </label>
            <input
              type="text"
              name="social"
              value={formData.social}
              onChange={handleChange}
              className="input-brutal"
              placeholder="@handle or URL"
              data-testid="guest-social-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-brutal w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="guest-submit-button"
          >
            {loading ? "Submitting..." : "Submit application"}
            <Send size={14} />
          </button>

          {status.type && (
            <div
              className={`flex items-center gap-2 font-mono-tcc text-xs ${
                status.type === "success" ? "text-[#C8FF3E]" : "text-[#FF3B30]"
              }`}
            >
              {status.type === "success" ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
              {status.message}
            </div>
          )}
        </form>
      </div>
    </section>
  );
};
